import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from "react";
import { AlertTriangle, Sparkles } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export type ConfirmOptions = {
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
};

type Pending = { options: ConfirmOptions; resolve: (value: boolean) => void };
const ConfirmContext = createContext<((options: ConfirmOptions) => Promise<boolean>) | null>(null);

/** Uniform confirmations: accessible modal with explicit cancellation and no native window.confirm. */
export function AppConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<Pending | null>(null);
  const current = useRef<Pending | null>(null);
  const confirm = useCallback((options: ConfirmOptions) => new Promise<boolean>((resolve) => {
    current.current?.resolve(false);
    const next = { options, resolve };
    current.current = next;
    setPending(next);
  }), []);
  const settle = (accepted: boolean) => {
    if (!current.current) return;
    const item = current.current;
    current.current = null;
    setPending(null);
    item.resolve(accepted);
  };
  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog open={pending !== null} onOpenChange={open => { if (!open) settle(false); }}>
        <AlertDialogContent className="border-mystic-purple/40 bg-card/95 backdrop-blur-xl text-foreground shadow-glow rounded-xl max-w-md">
          <AlertDialogHeader>
            <div className="mb-2 flex h-11 w-11 items-center justify-center rounded-full border border-mystic-purple/30 bg-mystic-purple/10">
              {pending?.options.destructive
                ? <AlertTriangle className="h-5 w-5 text-destructive" aria-hidden="true" />
                : <Sparkles className="h-5 w-5 text-mystic-purple" aria-hidden="true" />}
            </div>
            <AlertDialogTitle className="font-mystical text-xl text-foreground">{pending?.options.title}</AlertDialogTitle>
            <AlertDialogDescription className="font-ethereal leading-relaxed text-muted-foreground">
              {pending?.options.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-border" onClick={() => settle(false)}>
              {pending?.options.cancelLabel ?? "Cancelar"}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => settle(true)}
              className={pending?.options.destructive
                ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
                : "bg-gradient-mystic text-primary-foreground shadow-glow"}
            >
              {pending?.options.confirmLabel ?? "Confirmar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}

export function useAppConfirm() {
  const context = useContext(ConfirmContext);
  if (!context) throw new Error("useAppConfirm necesita AppConfirmProvider");
  return context;
}
